/**
 * useChanges — the React binding for the Change Feed.
 *
 * Strict layering: the page calls THIS hook; the hook calls {@link ChangeStore}
 * (and the existing {@link GoalStore} / {@link WorkstreamStore} to resolve
 * linked goal/workstream titles CLIENT-SIDE for the feed cards + the pickers);
 * the stores call the api facade. The page never imports a store or the api
 * directly.
 *
 * The feed is ordered by recency only — there is deliberately no per-engineer
 * count/velocity/ranking. Entries are consumed for their why/impact context.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useApi } from '../../hooks/useApi';
import { GoalStore } from '../goals/goalStore';
import { WorkstreamStore } from '../workstreams/workstreamStore';
import { ChangeStore } from './changeStore';
import { sortByRecency } from './changeModel';
import type {
  EngineeringChange,
  EngineeringChangeDraft,
  EngineeringChangeSummary,
} from './types';
import type { GoalSummary } from '../goals/types';
import type { WorkstreamSummary } from '../workstreams/types';

export interface UseChangesResult {
  /** All non-deleted change summaries (newest-first). */
  changes: EngineeringChangeSummary[];
  /** Goal summaries (resolve linked goal titles + goal picker). */
  goals: GoalSummary[];
  /** Workstream summaries (resolve linked workstream titles + picker). */
  workstreams: WorkstreamSummary[];
  /** Current user's alias (engineer default on the log-a-change form). */
  currentAlias: string;
  /** Map of goalId -> title for feed rendering. */
  goalTitles: Map<string, string>;
  /** Map of workstreamId -> title for feed rendering. */
  workstreamTitles: Map<string, string>;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  create: (draft: EngineeringChangeDraft) => Promise<EngineeringChange>;
  remove: (id: string) => Promise<void>;
}

export function useChanges(): UseChangesResult {
  const api = useApi();
  const stores = useMemo(
    () => ({
      changes: new ChangeStore(api),
      goals: new GoalStore(api),
      workstreams: new WorkstreamStore(api),
    }),
    [api],
  );

  const [changes, setChanges] = useState<EngineeringChangeSummary[]>([]);
  const [goals, setGoals] = useState<GoalSummary[]>([]);
  const [workstreams, setWorkstreams] = useState<WorkstreamSummary[]>([]);
  const [currentAlias, setCurrentAlias] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [rows, goalRows, wsRows, identity] = await Promise.all([
        stores.changes.listSummaries(),
        stores.goals.listSummaries(),
        stores.workstreams.listSummaries(),
        api.identityProvider.getIdentity(),
      ]);
      setChanges(sortByRecency(rows));
      setGoals(goalRows);
      setWorkstreams(wsRows);
      setCurrentAlias(identity.alias);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load the change feed.');
    } finally {
      setLoading(false);
    }
  }, [stores, api]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const goalTitles = useMemo(() => {
    const m = new Map<string, string>();
    for (const g of goals) m.set(g.id, g.title);
    return m;
  }, [goals]);

  const workstreamTitles = useMemo(() => {
    const m = new Map<string, string>();
    for (const w of workstreams) m.set(w.id, w.title);
    return m;
  }, [workstreams]);

  const create = useCallback(
    async (draft: EngineeringChangeDraft) => {
      const saved = await stores.changes.create(draft);
      await refresh();
      return saved;
    },
    [stores, refresh],
  );

  const remove = useCallback(
    async (id: string) => {
      await stores.changes.softDelete(id);
      await refresh();
    },
    [stores, refresh],
  );

  return {
    changes,
    goals,
    workstreams,
    currentAlias,
    goalTitles,
    workstreamTitles,
    loading,
    error,
    refresh,
    create,
    remove,
  };
}
